import React, { useState, useEffect, useCallback } from "react";
import FlagIcon from "@mui/icons-material/Flag";
import { getConfirmDeleteDialog } from "../Components/confirmDeleteDialog";
import MainLayout from "../Themes/MainLayout";
import {
  Typography,
  Button,
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Popover,
  Switch,
  Paper,
  Pagination,
  CssBaseline,
  MenuItem,
  Menu,
  InputAdornment,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import { Add, Search, FilterList } from "@mui/icons-material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as Settings } from "../Icons/Settings.svg";
import { ThemeProvider } from "@mui/material/styles";
import FormThemes from "../Themes/FormThemes";
import Table_theme from "../Themes/TableTheme";
import UserGroupAssignmentManager from "../Components/UserGroupAssignmentManager";
import GroupIcon from "@mui/icons-material/Group";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const columns = ["emp_id", "employee_name", "email", "is_active"];

const columnLabels = {
  emp_id: "Employee ID",
  employee_name: "Employee Name",
  email: "Email",
  password_hash: "Password",
  is_active: "Active",
};

function Users() {
  const { boId } = useCurrentUser();
  const [data, setData] = useState([]);
  const formFields = ["bo_id", "emp_id", "email", "password_hash", "is_active"];
  const [visibleCols, setVisibleCols] = useState(
    Object.fromEntries(columns.map((c) => [c, true]))
  );
  const [filters, setFilters] = useState({ is_active: "ACTIVE" });
  const [filterAnchors, setFilterAnchors] = useState({});
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [alert, setAlert] = useState({ type: "", msg: "" });
  const [anchorSettings, setAnchorSettings] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [organizations, setOrganizations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [userGroupDialogOpen, setUserGroupDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const query = new URLSearchParams({
        page,
        limit: rowsPerPage,
        ...filters,
      }).toString();
      const res = await fetch(`${BASE_URL}/api/users/paginated?${query}`);
      const json = await res.json();
      setData(json.data || []);
      setTotalPages(Math.ceil(json.total / json.limit));
    } catch (err) {
      console.error("Error fetching users:", err);
      setAlert({ type: "error", msg: "Failed to load users" });
    }
  }, [page, filters]);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const res = await fetch(`${BASE_URL}/Organization`);
        const data = await res.json();
        setOrganizations(data || []);
        // If only one org, set it by default in formData
        if (data.length === 1) {
          setFormData((prev) => ({
            ...prev,
            bo_id: data[0].bo_id,
          }));
        }
      } catch (err) {
        console.error("Error fetching organizations:", err);
      }
    };
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (!boId) return;
    fetch(`${BASE_URL}/EmployeeDetails/all/${boId}`)
      .then((res) => res.json())
      .then((result) => {
        if (Array.isArray(result.data)) {
          setEmployees(result.data);
        } else {
          setEmployees([]); // fallback
        }
      })
      .catch(() => setEmployees([]));
  }, [boId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    if (alert.msg) {
      const timer = setTimeout(() => {
        setAlert({ type: "", msg: "" });
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [alert.msg]);

  useEffect(() => {
    const fetchStatusFlags = async () => {
      try {
        const res = await fetch(`${BASE_URL}/commonVariables/statusFlag`);
        const data = await res.json();
        setStatusOptions(data); // [{ code: "ACTIVE", label: "Active" }, ...]
      } catch (err) {
        console.error("Failed to fetch employee status flags:", err);
      }
    };
    fetchStatusFlags();
  }, []);

  const handleAdd = () => {
    const selectedBoId = boId || organizations[0]?.bo_id || "";
    setFormData({ is_active: true, bo_id: selectedBoId });
    setIsEditing(false);
    setOpenDialog(true);
  };

  const handleEdit = (item) => {
    setFormData(item);
    setIsEditing(true);
    setOpenDialog(true);
  };

  const handleDelete = (item) => {
    setSelectedUser(item);
    setDeleteId(item.user_id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await fetch(`${BASE_URL}/api/users/${deleteId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Delete failed");
      await fetchData();
      setAlert({ type: "success", msg: "User deleted successfully" });
    } catch (err) {
      console.error("Error deleting user:", err);
      setAlert({ type: "error", msg: "Failed to delete user" });
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
      setSelectedUser(null);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      const res = await fetch(`${BASE_URL}/api/users/${item.user_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...item,
          is_active: !item.is_active,
        }),
      });
      if (!res.ok) throw new Error("Toggle failed");
      await fetchData();
      setAlert({ type: "success", msg: "Toggle flag updated Successfully.." });
    } catch (err) {
      setAlert({ type: "error", msg: "Failed to update status" });
    }
  };

  const handleSave = async () => {
    try {
      const method = isEditing ? "PUT" : "POST";
      const url = isEditing
        ? `${BASE_URL}/api/users/${formData.user_id}`
        : `${BASE_URL}/api/users`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        throw new Error("Save failed");
      }
      await fetchData();
      setOpenDialog(false);
      setAlert({
        type: "success",
        msg: isEditing
          ? "Record Updated successfully!"
          : "Record added successfully!",
      });
    } catch (err) {
      setAlert({ type: "error", msg: "This user already exists" });
    }
  };

  const handleUserGroupDialogClose = () => {
    setUserGroupDialogOpen(false);
    setSelectedUser(null);
    fetchData(); // Refresh data to update counts
  };

  const handleManageGroups = (user) => {
    setSelectedUser(user);
    setUserGroupDialogOpen(true);
  };

  return (
    <ThemeProvider theme={Table_theme}>
      <CssBaseline />
      <MainLayout>
        {alert.msg && (
          <Box mb={2}>
            <Alert
              onClose={() => setAlert({ type: "", msg: "" })}
              variant="standard"
              sx={{
                color: alert.type === "success" ? "#2E9F50" : "#FF0C0C",
                fontWeight: 500,
                backgroundColor: "#fff",
              }}
            >
              {alert.msg}
            </Alert>
          </Box>
        )}
        <Typography variant="h4" gutterBottom fontSize="25px" fontWeight="600">
          Users
        </Typography>

        <Box display="flex" alignItems="center" mb={2} gap={1.5}>
          <Button
            onClick={(e) => setAnchorSettings(e.currentTarget)}
            sx={{ minWidth: 0, p: 1 }}
          >
            <Settings />
          </Button>
          <Menu
            anchorEl={anchorSettings}
            open={Boolean(anchorSettings)}
            onClose={() => setAnchorSettings(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 1,
                p: 1,
                width: 240,
                maxHeight: 400,
                overflowY: "auto",
                borderRadius: 2,
              },
            }}
          >
            {columns.map((col) => (
              <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    border: "1px solid #ccc",
                    borderRadius: 1.5,
                    px: 2,
                    py: 1,
                    backgroundColor: "#fff",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color: "#333",
                      fontWeight: "bold",
                    }}
                  >
                    {columnLabels[col]}
                  </Typography>
                  <Switch
                    size="small"
                    checked={visibleCols[col]}
                    onChange={(e) =>
                      setVisibleCols((prev) => ({
                        ...prev,
                        [col]: e.target.checked,
                      }))
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#2DB0E4",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#2DB0E4",
                        },
                    }}
                  />
                </Box>
              </MenuItem>
            ))}
          </Menu>
          {/* Add Button */}
          <Button
            variant="contained"
            size="small"
            onClick={handleAdd}
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              px: 2.5,
              height: 36,
              backgroundColor: "#2DB0E4",
            }}
            startIcon={<Add />}
          >
            Add
          </Button>

          {/* Search */}
          <TextField
            size="small"
            placeholder="Search…"
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, search: e.target.value }));
              setPage(1);
            }}
            sx={{
              width: 250,
              "& input": { fontSize: 14 }, // input + placeholder
              "& input::placeholder": { fontSize: 12 }, // placeholder only
            }}
            InputProps={{
              sx: {
                height: 36, // Reduce overall input height
                paddingRight: 0,
              },
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {/* Status Dropdown */}
          <TextField
            select
            size="small"
            label="Status"
            value={filters.is_active}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, is_active: e.target.value }))
            }
            sx={{
              width: 200,
              // Make height match the 36px of the search field
              "& .MuiInputBase-root": {
                height: 36,
              },

              // Fix vertical alignment of text
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                fontSize: 12,
                padding: "0 14px",
              },

              // Label font size
              "& .MuiInputLabel-root": { fontSize: "14px" },
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      fontSize: "14px",
                      minHeight: "32px",
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="All">All</MenuItem>
            {statusOptions.map((opt) => (
              <MenuItem key={opt.code} value={opt.code}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {/* Table */}
        <Box
          sx={{ width: "100%", overflowX: "auto", px: 0, py: 2, mt: "-10px" }}
        >
          {getConfirmDeleteDialog({
            open: confirmOpen,
            onClose: () => {
              setConfirmOpen(false);
            },
            onConfirm: confirmDelete,
            title: "Confirm Deletion",
            contentText: selectedUser
              ? `Are you sure you want to permanently delete the employee ${
                  selectedUser.first_name || ""
                } ${selectedUser.last_name || ""}?`
              : "Are you sure you want to permanently delete this employee?",
          })}

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ textAlign: "center" }}>
                    <strong>Actions</strong>
                  </TableCell>
                  {columns.map(
                    (col) =>
                      visibleCols[col] && (
                        <TableCell key={col}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <strong>{columnLabels[col]}</strong>
                            <IconButton
                              size="small"
                              onClick={(e) =>
                                setFilterAnchors({
                                  ...filterAnchors,
                                  [col]: e.currentTarget,
                                })
                              }
                            >
                              <FilterList fontSize="small" />
                            </IconButton>
                            <Popover
                              open={!!filterAnchors[col]}
                              anchorEl={filterAnchors[col]}
                              onClose={() =>
                                setFilterAnchors({
                                  ...filterAnchors,
                                  [col]: null,
                                })
                              }
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                            >
                              <Box p={2}>
                                <TextField
                                  label={`Filter ${columnLabels[col]}`}
                                  size="small"
                                  value={filters[col] || ""}
                                  onChange={(e) =>
                                    setFilters({
                                      ...filters,
                                      [col]: e.target.value,
                                    })
                                  }
                                />
                                <Box
                                  mt={1}
                                  display="flex"
                                  justifyContent="flex-end"
                                  gap={1}
                                >
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() =>
                                      setFilterAnchors({
                                        ...filterAnchors,
                                        [col]: null,
                                      })
                                    }
                                  >
                                    Apply
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      const newFilters = { ...filters };
                                      delete newFilters[col];
                                      setFilters(newFilters);
                                      setFilterAnchors({
                                        ...filterAnchors,
                                        [col]: null,
                                      });
                                    }}
                                  >
                                    Clear
                                  </Button>
                                </Box>
                              </Box>
                            </Popover>
                          </Box>
                        </TableCell>
                      )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length > 0 ? (
                  data.map((item) => (
                    <TableRow key={item.user_id}>
                      <TableCell>
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(item)}
                              sx={{ color: "#2DB0E4" }}
                            >
                              <EditOutlinedIcon sx={{ fontSize: "16px" }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Assign to Groups">
                            <IconButton
                              size="small"
                              onClick={() => handleManageGroups(item)}
                              sx={{ color: "#759bfdff" }}
                            >
                              <GroupIcon sx={{ fontSize: "16px" }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(item)}
                            >
                              <DeleteIcon
                                sx={{ color: "#FF0C0C", width: 17, height: 17 }}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Toggle Delete Flag">
                            <IconButton
                              size="small"
                              onClick={() => handleToggleActive(item)}
                            >
                              <FlagIcon
                                style={{
                                  color: item.is_active ? "#2E9F50" : "#FF0C0C",
                                  transition: "color 0.2s",
                                  width: 17,
                                  height: 17,
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      {columns.map(
                        (col) =>
                          visibleCols[col] && (
                            <TableCell key={col}>
                              {col === "password_hash"
                                ? "•".repeat(item[col]?.length || 15)
                                : col === "is_active"
                                ? item[col]
                                  ? "Yes"
                                  : "No"
                                : col === "employee_name"
                                ? `${item.first_name || ""} ${
                                    item.last_name || ""
                                  }`.trim()
                                : item[col]}
                            </TableCell>
                          )
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center">
                      No matching data found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Pagination */}
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, v) => setPage(v)}
              color="primary"
              variant="outlined"
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: "8px",
                  minWidth: "32px",
                  height: "32px",
                  fontSize: "14px",
                },
                "& .Mui-selected": {
                  backgroundColor: "#b3dbea !important",
                  color: "black",
                  fontWeight: "bold",
                },
                "& .MuiPaginationItem-ellipsis": { display: "none" },
              }}
            />
          </Box>
        </Box>
        {/* Add/Edit Dialog */}
        <ThemeProvider theme={FormThemes}>
          <CssBaseline />
          <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            PaperProps={{
              sx: {
                width: 400,
                maxWidth: 400,
              },
            }}
          >
            <DialogTitle>
              {isEditing
                ? `Update User: ${
                    formData.username ||
                    `${formData.first_name || ""} ${
                      formData.last_name || ""
                    }`.trim() ||
                    "User"
                  }`
                : "Add New User"}
            </DialogTitle>
            <DialogContent>
              {/* 1. Organization (bo_id) */}
              <Box mb={1} sx={{ maxWidth: 350 }}>
                <TextField
                  label="Organization"
                  fullWidth
                  size="small"
                  margin="dense"
                  value={
                    organizations.find((org) => org.bo_id === formData.bo_id)
                      ?.bo_name || ""
                  }
                  disabled
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline legend": {
                      padding: 0,
                      maxWidth: "70px", //  remove extra notch space
                    },
                  }}
                />
              </Box>
              {/* 2. Employee (emp_id) */}
              <Box mb={1} sx={{ maxWidth: 350 }}>
                <Autocomplete
                  options={employees}
                  getOptionLabel={(option) =>
                    `${option.emp_id} - ${option.first_name} ${option.last_name}`
                  }
                  value={
                    formData.emp_id
                      ? {
                          emp_id: formData.emp_id,
                          first_name: formData.first_name,
                          last_name: formData.last_name,
                        }
                      : null
                  }
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setFormData((prev) => ({
                        ...prev,
                        emp_id: newValue.emp_id,
                        first_name: newValue.first_name,
                        last_name: newValue.last_name,
                        email: newValue.bfx_email,
                        bo_id: prev.bo_id || boId || organizations[0]?.bo_id,
                      }));
                    } else {
                      setFormData((prev) => ({
                        ...prev,
                        emp_id: "",
                        first_name: "",
                        last_name: "",
                        email: "",
                      }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Employee"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        "& .MuiInputBase-input": { fontSize: "0.8rem" }, // input text size
                        "& .MuiInputLabel-root": { fontSize: "0.8rem" }, // label size
                        "& .MuiOutlinedInput-notchedOutline legend": {
                          padding: 0,
                          maxWidth: "90px", //  remove extra notch space
                        },
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.emp_id === value.emp_id
                  }
                  slotProps={{
                    paper: {
                      sx: {
                        "& .MuiAutocomplete-option": {
                          fontSize: "0.8rem",
                          padding: "4px 8px",
                        },
                      },
                    },
                  }}
                />
              </Box>
              {/* 3. Email (auto-filled, read-only) */}
              <Box mb={1} sx={{ maxWidth: 350 }}>
                <TextField
                  label="Email"
                  name="email"
                  fullWidth
                  size="small"
                  value={formData.email || ""}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline legend": {
                      padding: 0,
                      maxWidth: "35px", //  remove extra notch space
                    },
                  }}
                  InputProps={{ readOnly: true }}
                />
              </Box>
              {/* 4. Rest of the fields */}
              {formFields
                .filter(
                  (col) =>
                    col !== "bo_id" && col !== "emp_id" && col !== "email"
                )
                .map((col, idx) => (
                  <Box key={idx} mb={1} sx={{ maxWidth: 350 }}>
                    {col === "is_active" ? (
                      <Box display="flex" alignItems="center">
                        <Switch
                          checked={!!formData.is_active}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              is_active: e.target.checked,
                            })
                          }
                        />
                        <Typography ml={1}>Active</Typography>
                      </Box>
                    ) : (
                      <TextField
                        label={columnLabels[col]}
                        name={col}
                        fullWidth
                        size="small"
                        type={col === "password_hash" ? "password" : "text"}
                        value={
                          col === "password_hash" && !formData.isEditingPassword
                            ? "•".repeat(formData.password_length || 15)
                            : formData[col] || ""
                        }
                        onFocus={() => {
                          if (
                            col === "password_hash" &&
                            !formData.isEditingPassword
                          ) {
                            setFormData({
                              ...formData,
                              password_hash: "", // clear dots on focus
                              isEditingPassword: true, // flag to start editing
                            });
                          }
                        }}
                        onChange={(e) =>
                          setFormData({ ...formData, [col]: e.target.value })
                        }
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          "& .MuiOutlinedInput-notchedOutline legend": {
                            padding: 0,
                            maxWidth: "55px", //  remove extra notch space
                          },
                        }}
                      />
                    )}
                  </Box>
                ))}
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setOpenDialog(false)} size="small">
                Cancel
              </Button>
              <Button onClick={handleSave} variant="contained" size="small">
                {isEditing ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
        {selectedUser && (
          <UserGroupAssignmentManager
            open={userGroupDialogOpen}
            onClose={handleUserGroupDialogClose}
            userId={selectedUser.user_id}
            userName={`${selectedUser.first_name || ""} ${
              selectedUser.last_name || ""
            }`}
          />
        )}
      </MainLayout>
    </ThemeProvider>
  );
}

export default Users;
